import React, { useState, useEffect } from 'react';
import { Calendar, Users, BookOpen, Clock, Settings as SettingsIcon, Database, Check, ArrowLeft, Menu, Home, Target, RefreshCw } from 'lucide-react';
import timetableService from './services/timetableService';

// Import styles
import './styles/TimetablePlannerMain.css';
import './styles/TimetablePlannerDashboard.css';
import { CSS_CLASSES, MODULE_THEMES, STATUS_COLORS } from './styles/index.js';

// Import component modules
import Preparation from './components/Preparation';
import Planner from './components/Planner';
import Settings from './components/Settings';
import PlanningData from './components/PlanningData';
import Generation from './components/Generation';
import DataSetPreparation from './components/DataSetPreparation';
import SchoolTimetableManagement from './components/SchoolTimetableManagement';

const MyClassTimetablePlanner = ({ currentUser, onBack, onTabChange }) => {
  const [currentModule, setCurrentModule] = useState('preparation');
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [selectedDataSet, setSelectedDataSet] = useState(null);
  const [newDataSetName, setNewDataSetName] = useState('');

  // Data states for Supabase integration
  const [timetableSetup, setTimetableSetup] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjectGroups, setSubjectGroups] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [dataSets, setDataSets] = useState([]);
  const [loading, setLoading] = useState(false);

  const modules = [
    { id: 'preparation', name: '準備 (Preparation)', icon: Home },
    { id: 'planner', name: '策劃 (Planner)', icon: Calendar },
    { id: 'settings', name: '設定 (Settings)', icon: SettingsIcon },
    { id: 'planning', name: '計劃資料 (Planning Data)', icon: BookOpen },
    { id: 'generation', name: '編製產生 (Generation)', icon: RefreshCw },
    { id: 'dataset', name: '建立資料集 (Data Set)', icon: Database },
    { id: 'management', name: '學校時間表管理系統', icon: Target }
  ];

  // Load data from Supabase
  useEffect(() => {
    loadTimetableData();
  }, []);

  const loadTimetableData = async () => {
    setLoading(true);
    try {
      // Load all data in parallel using the service
      const [
        setupData,
        roomsData,
        teachersData,
        classesData,
        groupsData,
        classSubjectsData,
        dataSetsData
      ] = await Promise.all([
        timetableService.getTimetableSetup().catch(() => null),
        timetableService.getRooms(),
        timetableService.getTeachers(),
        timetableService.getClasses(),
        timetableService.getSubjectGroups(),
        timetableService.getClassSubjectLessons(),
        timetableService.getDataSets()
      ]);

      if (setupData) setTimetableSetup(setupData);
      if (roomsData) setRooms(roomsData);
      if (teachersData) setTeachers(teachersData);
      if (classesData) setClasses(classesData);
      if (groupsData) setSubjectGroups(groupsData);
      if (classSubjectsData) setClassSubjects(classSubjectsData);
      if (dataSetsData) setDataSets(dataSetsData);

    } catch (error) {
      console.error('Error loading timetable data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDataSet = async () => {
    if (newDataSetName.trim() && newDataSetName.length <= 8) {
      try {
        const data = await timetableService.createDataSet({
          data_set_name: newDataSetName.trim(),
          status: 'Draft',
          description: 'New timetable dataset'
        });

        setDataSets(prev => [...prev, data]);
        setSelectedDataSet(data);
        setNewDataSetName('');
        setShowDialog(true);
        setDialogType('dataset-created');
      } catch (error) {
        console.error('Error creating dataset:', error);
      }
    }
  };

  const handleOpenDataSet = (dataSet) => {
    setSelectedDataSet(dataSet);
    setShowDialog(true);
    setDialogType('dataset-opened');
  };

  const handleModuleChange = (moduleId) => {
    setCurrentModule(moduleId);
  };

  const renderStepContent = () => {
    switch (currentModule) {
      case 'preparation':
        return <Preparation onNext={handleModuleChange} />;
      case 'planner':
        return <Planner onNext={handleModuleChange} />;
      case 'settings':
        return <Settings onNext={handleModuleChange} />;
      case 'planning':
        return <PlanningData onNext={handleModuleChange} />;
      case 'generation':
        return <Generation onNext={handleModuleChange} />;
      case 'dataset':
        return (
          <DataSetPreparation
            dataSets={dataSets}
            selectedDataSet={selectedDataSet}
            setSelectedDataSet={setSelectedDataSet}
            newDataSetName={newDataSetName}
            setNewDataSetName={setNewDataSetName}
            handleCreateDataSet={handleCreateDataSet}
            handleOpenDataSet={handleOpenDataSet}
          />
        );
      case 'management':
        return <SchoolTimetableManagement onBack={onBack} />;
      default:
        return <Preparation onNext={handleModuleChange} />;
    }
  };

  const renderDialog = () => {
    if (!showDialog) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
          {dialogType === 'dataset-created' && (
            <>
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Data Set Created</h3>
              <div className="mb-4 text-center">
                <div className="text-green-600 mb-2">
                  <Check size={48} className="mx-auto" />
                </div>
                <p className="text-gray-700">資料集 "{selectedDataSet?.data_set_name}" 已成功建立。</p>
                <p className="text-sm text-gray-500 mt-2">新的資料集目錄已在 DATA 目錄下建立。</p>
              </div>
              <button 
                onClick={() => setShowDialog(false)}
                className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200">
                OK
              </button>
            </>
          )}

          {dialogType === 'dataset-opened' && (
            <>
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Data Set Opened</h3>
              <div className="mb-4 text-center">
                <div className="text-blue-600 mb-2">
                  <Database size={48} className="mx-auto" />
                </div>
                <p className="text-gray-700">已開啟資料集 "{selectedDataSet?.data_set_name}"。</p>
                <p className="text-sm text-gray-500 mt-2">現在可以編修此資料集的內容。</p>
              </div>
              <button 
                onClick={() => setShowDialog(false)}
                className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200">
                OK
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw size={48} className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">載入時間表資料中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={CSS_CLASSES.TIMETABLE_PLANNER_PAGE}>
      {/* Header */}
      <div className={CSS_CLASSES.TIMETABLE_PLANNER_HEADER}>
        <div className="header-content">
          <div className="header-left">
            <button
              onClick={onBack}
              className={CSS_CLASSES.BACK_BTN}
            >
              <ArrowLeft size={20} />
              返回主頁
            </button>
            <div className="timetable-info">
              <h1>時間表編排 (Timetable Planner)</h1>
              <p className="timetable-subtitle">智能時間表編排系統</p>
            </div>
          </div>
          <div className="header-right">
            <span className="welcome-text">Welcome, {currentUser?.name || 'User'}</span>
            <div className="user-icon">
              <Check size={16} />
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className={CSS_CLASSES.TIMETABLE_PLANNER_CONTENT}>
        <div className={CSS_CLASSES.OVERVIEW_GRID}>
          <div className={CSS_CLASSES.OVERVIEW_CARD}>
            <div className="stat-content">
              <div className="stat-icon purple">
                <Users size={24} />
              </div>
              <div className="stat-details">
                <div className="stat-count">{teachers.length}</div>
                <div className="stat-label">總教師數</div>
                <div className="stat-subtitle">全職教師</div>
              </div>
            </div>
          </div>
          <div className={CSS_CLASSES.OVERVIEW_CARD}>
            <div className="stat-content">
              <div className="stat-icon green">
                <BookOpen size={24} />
              </div>
              <div className="stat-details">
                <div className="stat-count">{subjectGroups.length}</div>
                <div className="stat-label">科目組別</div>
                <div className="stat-subtitle">科目分組</div>
              </div>
            </div>
          </div>
          <div className={CSS_CLASSES.OVERVIEW_CARD}>
            <div className="stat-content">
              <div className="stat-icon blue">
                <Target size={24} />
              </div>
              <div className="stat-details">
                <div className="stat-count">{classes.length}</div>
                <div className="stat-label">班別數</div>
                <div className="stat-subtitle">全校班別</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Horizontal Module Navigation */}
      <div className="horizontal-module-nav">
        <div className="module-nav-header">
          <h2 className="module-nav-title">模組 (Modules)</h2>
        </div>
        <div className={CSS_CLASSES.MODULE_NAV_CONTAINER}>
          {modules.map(module => {
            const Icon = module.icon;
            return (
              <button
                key={module.id}
                onClick={() => handleModuleChange(module.id)}
                className={`${CSS_CLASSES.MODULE_NAV_BTN} ${
                  currentModule === module.id ? 'active' : ''
                }`}>
                <span className={CSS_CLASSES.MODULE_NAV_ICON}>
                  <Icon size={18} />
                </span>
                <span className={CSS_CLASSES.MODULE_NAV_TEXT}>
                  {module.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <div className="main-content-container">
        <div className="bg-white rounded-lg shadow-sm border">
          {renderStepContent()}
        </div>
      </div>

      {renderDialog()}
    </div>
  );
};

export default MyClassTimetablePlanner;
